import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
          const variantData = JSON.parse(textContent);
          this.updateProductBarcode(variantData);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // 获取variant数据并更新SKU
          const variantData = JSON.parse(textContent);

          if (this.selectedOptionId) {
            // 分发variant更新事件
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
            
            // 更新SKU显示
            this.updateProductSku(variantData);
            
            // 更新UPC/barcode显示
            this.updateProductBarcode(variantData);
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * 更新产品SKU显示
   * @param {Object} variantData - Variant数据
   */
  updateProductSku(variantData) {
    // 从variant数据中获取SKU
    const sku = variantData.sku || '';
    
    // 查找并更新所有SKU显示元素
    document.querySelectorAll('#product-sku, [data-product-sku]').forEach((skuElement) => {
      skuElement.textContent = sku;
      
      // 添加更新动画
      skuElement.classList.remove('sku-updated');
      void skuElement.offsetWidth;
      skuElement.classList.add('sku-updated');
    });
  }

  /**
   * 更新产品UPC/barcode显示
   * @param {Object} variantData - Variant数据
   */
  updateProductBarcode(variantData) {
    const barcode = variantData.barcode || '';
    const variantId = variantData.id || '';
    
    // 分发自定义事件来更新barcode显示
    this.dispatchEvent(new CustomEvent('variant:change', {
      detail: {
        variant: {
          id: variantId,
          barcode: barcode
        }
      },
      bubbles: true
    }));
    
    // 同时也可以直接更新现有的barcode元素
    const barcodeElements = document.querySelectorAll('[data-barcode-container]');
    
    barcodeElements.forEach((container) => {
      // 更新UPC文本
      const textElement = container.querySelector('.barcode-text');
      if (textElement) {
        textElement.textContent = `UPC Code: ${barcode}`;
        textElement.id = `barcode-text-${variantId}`;
      }
      
      // 更新SVG元素
      const svgElement = container.querySelector('.display-barcode-svg');
      if (svgElement && typeof JsBarcode !== 'undefined') {
        svgElement.id = `display-barcode-svg-${variantId}`;
        svgElement.setAttribute('data-barcode', barcode);
        svgElement.setAttribute('data-variant-id', variantId);
        
        // 重新生成条码
        if (barcode) {
          try {
            const cleanCode = barcode.toString().replace(/\D/g, '');
            let format = "CODE128";
            if (cleanCode.length === 12) format = "UPC";
            if (cleanCode.length === 13) format = "EAN13";
            if (cleanCode.length === 8) format = "EAN8";
            
            JsBarcode(`#display-barcode-svg-${variantId}`, barcode, {
              format: format,
              width: 1,
              height: 30,
              displayValue: true,
              text: barcode,
              fontSize: 10,
              fontOptions: "bold",
              textMargin: 2,
              lineColor: "#000000",
              background: "#ffffff",
              margin: 5
            });
            
            // 重新设置点击下载事件
            this.setupBarcodeDownload(svgElement, barcode, variantId);
            
            // 显示容器
            container.style.display = 'block';
          } catch (error) {
            console.error('Barcode update failed:', error);
            container.style.display = 'none';
          }
        } else {
          // 隐藏容器如果没有barcode
          container.style.display = 'none';
        }
      }
    });
  }

  /**
   * 设置条码下载功能
   * @param {SVGElement} svgElement - SVG元素
   * @param {string} barcode - 条码值
   * @param {string} variantId - Variant ID
   */
  setupBarcodeDownload(svgElement, barcode, variantId) {
    if (!svgElement || !barcode) return;
    
    // 移除现有的事件监听器
    const newSvgElement = svgElement.cloneNode(true);
    svgElement.parentNode.replaceChild(newSvgElement, svgElement);
    
    newSvgElement.addEventListener('click', (e) => {
      e.preventDefault();
      this.downloadBarcode(barcode, variantId);
    });
    
    newSvgElement.style.cursor = 'pointer';
    newSvgElement.addEventListener('mouseenter', function() {
      this.style.opacity = '0.8';
    });
    newSvgElement.addEventListener('mouseleave', function() {
      this.style.opacity = '1';
    });
  }

  /**
   * 下载条码图片
   * @param {string} barcode - 条码值
   * @param {string} variantId - Variant ID
   */
  downloadBarcode(barcode, variantId) {
    if (!barcode) return;
    
    try {
      const tempSvgId = 'temp-barcode-svg-' + Date.now();
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.setAttribute('id', tempSvgId);
      tempSvg.style.position = 'absolute';
      tempSvg.style.left = '-9999px';
      document.body.appendChild(tempSvg);
      
      const cleanCode = barcode.toString().replace(/\D/g, '');
      let format = "CODE128";
      if (cleanCode.length === 12) format = "UPC";
      if (cleanCode.length === 13) format = "EAN13";
      if (cleanCode.length === 8) format = "EAN8";
      
      JsBarcode('#' + tempSvgId, barcode, {
        format: format,
        width: 2,
        height: 100,
        displayValue: true,
        text: barcode,
        fontSize: 16,
        lineColor: "#000000",
        background: "#ffffff",
        margin: 10
      });
      
      setTimeout(() => {
        const svg = document.getElementById(tempSvgId);
        if (!svg) return;
        
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        canvas.width = svg.width.baseVal.value;
        canvas.height = svg.height.baseVal.value;
        
        img.onload = function() {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          
          const pngFile = canvas.toDataURL("image/png");
          const downloadLink = document.createElement("a");
          downloadLink.download = `barcode_${barcode}.png`;
          downloadLink.href = pngFile;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          document.body.removeChild(tempSvg);
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }, 100);
      
    } catch (error) {
      console.error('Download failed:', error);
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}